import React, { useState, useEffect, useMemo } from "react";
import UsuarioService, { ExpireAt } from "../../sdk/services/UsuarioService";
import {
  Button,
  Col,
  Typography,
  Form,
  Modal,
  Badge,
  Input,
  notification,
  Row,
  Calendar,
  Space,
  Card,
} from "antd";
import { useNavigate } from "react-router-dom";
import { Usuario } from "../../sdk/@types";
import DefaultLayout from "../layouts/default/Default.layout";
import Table from "../components/Table/Table";
import { Column, useTable } from "react-table";
import useUsuario from "../../core/hooks/UseUsuario";
import { CSS } from "@dnd-kit/utilities";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { readApiError } from "../../sdk/errors/apiError";
import { differenceInDays, format, isAfter, startOfToday } from "date-fns";
import {
  DndContext,
  rectIntersection,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import useCertame from "../../core/hooks/UseCertame";
import AssinanteService from "../../sdk/services/AssinanteService";
import { Assinante } from "../../sdk/@types/Assinante";

// --- Zod Schema ---
const subscriptionSchema = z.object({
  validUntil: z.date().refine((date) => isAfter(date, new Date()), {
    message: "A data de vencimento deve ser maior que a data de hoje",
  }),
  selectionProcessIDs: z
    .array(z.string())
    .min(1, "Selecione ao menos um certame"),
});

type SubscriptionForm = z.infer<typeof subscriptionSchema>;
const { Text } = Typography;

// --- Droppable Container Wrapper ---
const DroppableContainer = ({
  id,
  children,
  className,
}: {
  id: string;
  children: React.ReactNode;
  className: string;
}) => {
  const { setNodeRef } = useDroppable({ id });
  return (
    <div ref={setNodeRef} id={id} className={className}>
      {children}
    </div>
  );
};

// --- Sortable Item Component ---
const SortableItem = ({
  id,
  label,
  container,
  isSelected,
}: {
  id: string;
  label: string;
  container: string;
  isSelected?: boolean;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    data: { container },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 999 : undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="mb-2 touch-none"
    >
      <Card
        size="small"
        className={`cursor-grab active:cursor-grabbing border-2 transition-colors ${
          isSelected
            ? "border-blue-500 bg-blue-50"
            : "border-gray-200 hover:border-blue-400"
        }`}
      >
        <span className="font-bold text-gray-700">{label}</span>
      </Card>
    </div>
  );
};

export default function UsuariosList() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { usuarios, buscarUsuarios } = useUsuario();
  const [openModal, setOpenModal] = useState(false);
  const [userSelected, setUserSelected] = useState<Usuario.Usuario>();
  const { certames, getCertames } = useCertame();

  const [availableIds, setAvailableIds] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [validUntil, setValidUntil] = useState<Date | null>(null);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<SubscriptionForm>({
    resolver: zodResolver(subscriptionSchema),
    defaultValues: { selectionProcessIDs: [] },
  });

  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    buscarUsuarios();
    getCertames();
  }, [buscarUsuarios, getCertames]);

  // Sync available IDs when certames load
  useEffect(() => {
    if (certames.length > 0) {
      setAvailableIds(certames.map((c: any) => c.id));
    }
  }, [certames]);

  const toggleModal = () => {
    setOpenModal(!openModal);
    if (openModal) {
      reset(); // Clear form on close
      setValidUntil(null);
    }
  };

  async function resetPassword(userId: string) {
    const mensagem = await UsuarioService.setResetPassword(userId);

    if (!mensagem) {
      notification.error({
        message: "Erro",
        description:
          "Ocorreu um erro ao alterar a senha. Por favor, tente novamente.",
      });
      return;
    }

    notification.success({
      message: "Sucesso",
      // duration 0: a senha temporária só é devolvida uma vez, então o aviso
      // fica aberto até o admin fechar e copiar.
      duration: 0,
      description: mensagem.temporaryPassword
        ? `${mensagem.message}. Senha temporária: ${mensagem.temporaryPassword} — copie agora, ela não será exibida novamente.`
        : mensagem.message,
    });
  }

const onSave = async (data: SubscriptionForm) => {
  if (!userSelected?.id) {
    notification.error({
      message: "Erro",
      description: "Selecione um usuário antes de salvar",
    });
    return;
  }

  if (!data.validUntil || isNaN(data.validUntil.getTime())) {
    notification.error({
      message: "Erro",
      description: "Data inválida",
    });
    return;
  }

  if (!selectedIds.length) {
    notification.error({
      message: "Erro",
      description: "Selecione pelo menos um certame",
    });
    return;
  }

  try {
    const dataToSend: Assinante.Assinante = {
      userID: userSelected.id,
      validUntil: data.validUntil.toISOString(),
      selectionProcessIDs: selectedIds,
      disabled: false,
    };

    const response = await AssinanteService.create(dataToSend);

    if (response) {
      notification.success({
        message: "Sucesso",
        description: "Assinatura criada com sucesso",
      });
    }

    toggleModal();
    buscarUsuarios();
  } catch (error) {
    // Negativa de permissão vem antes: o interceptor já mostrou o aviso certo,
    // e "Erro: Request failed with status code 403" só confundiria por cima.
    if (readApiError(error).kind) return;

    if (error instanceof z.ZodError) {
      notification.error({
        message: "Erro de validação",
        description: error.issues.map((e) => e.message).join(", "),
      });
    } else if (error instanceof Error) {
      notification.error({
        message: "Erro",
        description: error.message,
      });
    } else {
      notification.error({
        message: "Erro",
        description: "Erro inesperado",
      });
    }
  }
};

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    const isOverRight =
      overId === "right-container" || selectedIds.includes(overId);
    const isOverLeft =
      overId === "left-container" || availableIds.includes(overId);

    if (availableIds.includes(activeId) && isOverRight) {
      const newSelected = [...selectedIds, activeId];
      setAvailableIds((prev) => prev.filter((id) => id !== activeId));
      setSelectedIds(newSelected);
      setValue("selectionProcessIDs", newSelected);
    } else if (selectedIds.includes(activeId) && isOverLeft) {
      const newSelected = selectedIds.filter((id) => id !== activeId);
      setSelectedIds(newSelected);
      setAvailableIds((prev) => [...prev, activeId]);
      setValue("selectionProcessIDs", newSelected);
    }
  };

  const columns: Column<Usuario.Usuario>[] = useMemo(
    () => [
      { Header: "ID", accessor: "id" },
      { Header: "Usuario", accessor: "userName" },
      { Header: "Nome", accessor: "fullName" },
      {
        Header: "Válido até",
        accessor: "validUntil",
        Cell: ({ value }) =>
          value ? (
            <strong>{new Date(value).toLocaleDateString("pt-BR")}</strong>
          ) : (
            "Indefinido"
          ),
      },
      {
        Header: "Ação",
        Cell: ({ row }: any) => (
          <Space>
            <Button
              type="primary"
              onClick={() =>
                navigate("/usuario", { state: { usuario: row.original } })
              }
            >
              Ver Detalhes
            </Button>
            <Button
              type="primary"
              onClick={() => resetPassword(row.original.id)}
            >
              Resetar Senha
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setUserSelected(row.original);
                toggleModal();
              }}
            >
              Liberar Acesso
            </Button>
          </Space>
        ),
      },
    ],
    [navigate],
  );

  const instance = useTable<Usuario.Usuario>({ columns, data: usuarios });
  const today = startOfToday();
  const daysDiff = validUntil ? differenceInDays(validUntil, today) : 0;

  return (
    <DefaultLayout>
      <div className="p-4">
        <Table instance={instance} />
      </div>

      <Modal
        title={
          <span className="text-xl font-bold border-b-2 border-blue-500 pb-1">
            Liberação de Acesso
          </span>
        }
        open={openModal}
        onCancel={toggleModal}
        width={900}
        footer={null}
        centered
      >
        <form onSubmit={handleSubmit(onSave)} className="space-y-6 mt-4">
          {/* User Info Header */}
          <div className="flex gap-4 bg-gray-50 p-4 rounded-lg border border-gray-100">
            <div className="w-1/4">
              <Text className="text-[10px] font-black text-gray-400 uppercase tracking-tighter">
                Usuário
              </Text>
              <Input
                value={userSelected?.id}
                disabled
                className="bg-white font-mono"
              />
            </div>
            <div className="flex-1">
              <Text className="text-[10px] font-black text-gray-400 uppercase tracking-tighter">
                Nome
              </Text>
              <Input
                value={userSelected?.fullName}
                disabled
                className="bg-white font-semibold"
              />
            </div>
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={rectIntersection}
            onDragEnd={handleDragEnd}
          >
            <div className="grid grid-cols-2 gap-8">
              <div className="flex flex-col">
                <h3 className="font-bold text-gray-500 mb-2 uppercase text-xs tracking-widest">
                  Certames Disponíveis
                </h3>
                <DroppableContainer
                  id="left-container"
                  className="border-2 border-dashed border-gray-200 rounded-2xl p-4 min-h-[280px] bg-gray-50/50"
                >
                  <SortableContext
                    items={availableIds}
                    strategy={verticalListSortingStrategy}
                  >
                    {availableIds.map((id) => (
                      <SortableItem
                        key={id}
                        id={id}
                        container="left"
                        label={
                          certames.find((c: any) => c.id === id)
                            ?.abbreviation ?? id
                        }
                      />
                    ))}
                  </SortableContext>
                </DroppableContainer>
              </div>

              <div className="flex flex-col">
                <h3 className="font-bold text-blue-600 mb-2 uppercase text-xs flex justify-between tracking-widest">
                  Certames Selecionados{" "}
                  <Badge count={selectedIds.length} color="#2563eb" />
                </h3>
                <DroppableContainer
                  id="right-container"
                  className="border-2 border-blue-100 rounded-2xl p-4 min-h-[280px] bg-blue-50/30"
                >
                  <SortableContext
                    items={selectedIds}
                    strategy={verticalListSortingStrategy}
                  >
                    {selectedIds.map((id) => (
                      <SortableItem
                        key={id}
                        id={id}
                        container="right"
                        isSelected
                        label={
                          certames.find((c: any) => c.id === id)
                            ?.abbreviation ?? id
                        }
                      />
                    ))}
                  </SortableContext>
                </DroppableContainer>
              </div>
            </div>
          </DndContext>

          {/* Date Picker Section */}
          <div className="pt-6 border-t border-gray-100">
            <div className="grid grid-cols-5 gap-8 items-start">
              <div className="col-span-3">
                <h3 className="font-bold text-gray-700 mb-3 text-xs uppercase tracking-wider text-center lg:text-left">
                  Selecione a Data Limite
                </h3>
                <div className="border rounded-2xl overflow-hidden shadow-sm bg-white p-2">
                  <Controller
                    name="validUntil"
                    control={control}
                    render={({ field }) => (
                      <Calendar
                        fullscreen={false}
                        onChange={(val) => {
                          const date = val.toDate();
                          field.onChange(date);
                          setValidUntil(date);
                        }}
                        disabledDate={(current) =>
                          current && current.valueOf() < Date.now()
                        }
                      />
                    )}
                  />
                </div>
                {errors.validUntil && (
                  <Text type="danger" className="text-xs">
                    {errors.validUntil.message}
                  </Text>
                )}
              </div>

              <div className="col-span-2 pt-8">
                <div
                  className={`p-6 rounded-3xl transition-all duration-500 shadow-xl ${validUntil ? "bg-blue-600" : "bg-gray-200"}`}
                >
                  <p
                    className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${validUntil ? "text-blue-100" : "text-gray-400"}`}
                  >
                    Validade do Acesso
                  </p>
                  <h2
                    className={`text-4xl font-black ${validUntil ? "text-white" : "text-gray-400"}`}
                  >
                    {validUntil ? `${daysDiff} Dias` : "-- Dias"}
                  </h2>
                  {validUntil && (
                    <p className="text-xs text-blue-200 mt-2 italic font-medium">
                      Expira em: {format(validUntil, "dd/MM/yyyy")}
                    </p>
                  )}
                </div>

                <div className="mt-4 flex flex-col gap-2">
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    className="w-full h-12 rounded-xl font-bold bg-blue-600 shadow-blue-200 border-none"
                  >
                    SALVAR ALTERAÇÕES
                  </Button>
                  <Button
                    onClick={toggleModal}
                    type="text"
                    className="w-full font-bold text-gray-400 hover:text-red-500"
                  >
                    CANCELAR
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </Modal>
    </DefaultLayout>
  );
}
